/**
 * Fuzzy Search Utilities
 * 
 * Provides fuzzy string matching to handle variations in search queries
 * Examples: "headwise" matches "head-wise", "user auth" matches "authentication for users"
 */

/**
 * Normalize text for fuzzy search comparison
 * - Lowercase
 * - Remove hyphens, underscores, punctuation
 * - Collapse multiple spaces
 */
export function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_]/g, '') // Remove hyphens and underscores
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Collapse spaces
    .trim();
}

/**
 * Token-based fuzzy matching
 * Returns true if all query tokens appear in text (order independent)
 * 
 * Examples:
 * - "headwise" matches "head-wise" 
 * - "user auth" matches "authentication for users"
 * 
 * @param text The text to search in
 * @param query The search query
 * @param threshold Matching threshold (0-1, currently unused but available for future tuning)
 * @returns true if query matches text fuzzily
 */
export function fuzzyMatch(
  text: string, 
  query: string, 
  threshold: number = 0.8
): boolean {
  const normalizedText = normalizeForSearch(text);
  const normalizedQuery = normalizeForSearch(query);
  
  // Direct substring match (most common case)
  if (normalizedText.includes(normalizedQuery)) {
    return true;
  }
  
  // Token-based matching: all query words must appear in text
  const queryTokens = normalizedQuery.split(' ').filter(t => t.length > 0);
  const textTokens = normalizedText.split(' ');
  
  return queryTokens.every(queryToken => 
    textTokens.some(textToken => 
      textToken.includes(queryToken) || queryToken.includes(textToken)
    )
  );
}

